/**
 * Übersicht.
 *
 * Die Startseite beantwortet vier Fragen, in dieser Reihenfolge:
 *   1. Wie steht das Jahr? (Einnahmen, Ausgaben, Ergebnis)
 *   2. Ist die Kleinunternehmergrenze in Sicht?
 *   3. Was ist offen — wer schuldet mir Geld, was schulde ich?
 *   4. Wo fehlt noch etwas? (Belege ohne Kategorie)
 *
 * Die Grenzprüfung steht bewusst weit oben. Wer die Grenze reißt, schuldet
 * rückwirkend Umsatzsteuer auf Rechnungen, die er ohne ausgewiesen hat —
 * und kann sie beim Kunden meist nicht mehr nachfordern. Das ist der
 * teuerste Fehler, den diese Software verhindern kann.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Seite } from '../bausteine/Layout';
import { Meldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf } from '../lib/abruf';
import { datum, jahrHeute, monatsname, zahl } from '../lib/format';
import { alsCent, euro, formatiereCent } from '../lib/geld';
import type { Euer, Grenzpruefung, Monatsuebersicht, OffenePosten } from '../lib/typen';

export function Uebersicht(): ReactNode {
  const [jahr, setzeJahr] = useState(jahrHeute());

  const euerAbruf = useAbruf<Euer>(`/api/auswertung/euer?jahr=${jahr}`);
  const grenzen = useAbruf<Grenzpruefung>(`/api/auswertung/grenzen?jahr=${jahr}`);
  const monate = useAbruf<Monatsuebersicht>(`/api/auswertung/monate?jahr=${jahr}`);
  const offen = useAbruf<OffenePosten>('/api/auswertung/offene-posten');

  const jahre = [jahrHeute() + 1, jahrHeute(), jahrHeute() - 1, jahrHeute() - 2];

  return (
    <Seite
      titel="Übersicht"
      untertitel={`Geschäftsjahr ${jahr}`}
      aktionen={
        <>
          <select
            value={jahr}
            onChange={(e) => setzeJahr(Number.parseInt(e.target.value, 10))}
            aria-label="Jahr"
            style={{ width: 'auto' }}
          >
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
          <Link to="/belege/neu" className="knopf haupt">
            Beleg erfassen
          </Link>
        </>
      }
    >
      <div className="stapel">
        <Grenzkarte abruf={grenzen} />
        <Kennzahlen abruf={euerAbruf} />

        <div className="raster zwei">
          <Monatskarte abruf={monate} />
          <OffeneKarte abruf={offen} />
        </div>

        <Positionskarte abruf={euerAbruf} jahr={jahr} />
      </div>
    </Seite>
  );
}

// =========================================================================
//  Kennzahlen
// =========================================================================

function Kennzahlen({ abruf }: { abruf: ReturnType<typeof useAbruf<Euer>> }): ReactNode {
  if (abruf.fehler) return <Meldung ton="gefahr">{abruf.fehler}</Meldung>;
  if (!abruf.daten) return <div className="karte"><Laedt /></div>;

  const e = abruf.daten;
  const ergebnisCent = alsCent(e.ergebnis);

  return (
    <>
      {e.hinweise.ohneKategorie > 0 ? (
        <Meldung
          ton="warnung"
          titel={`${e.hinweise.ohneKategorie} ${e.hinweise.ohneKategorie === 1 ? 'Beleg' : 'Belege'} ohne Kategorie.`}
        >
          Ohne Kategorie zählt ein Beleg in keiner EÜR-Position mit.{' '}
          <Link to="/belege">In der Belegliste nachtragen</Link>.
        </Meldung>
      ) : null}

      <div className="raster vier">
        <div className="karte kennzahl">
          <div className="beschriftung">Einnahmen</div>
          <div className="wert">{e.anzeige.einnahmen}</div>
          <div className="zusatz">nach Zuflussprinzip</div>
        </div>

        <div className="karte kennzahl">
          <div className="beschriftung">Ausgaben</div>
          <div className="wert">{e.anzeige.ausgaben}</div>
          <div className="zusatz">nach Privatanteil und Kürzung</div>
        </div>

        <div className={`karte kennzahl ${ergebnisCent >= 0 ? 'gut' : 'gefahr'}`}>
          <div className="beschriftung">Ergebnis</div>
          <div className="wert">{e.anzeige.ergebnis}</div>
          <div className="zusatz">Einnahmen minus Ausgaben</div>
        </div>

        <div className="karte kennzahl">
          <div className="beschriftung">Offene Posten</div>
          <div className="wert">{e.hinweise.offenePosten.anzahl}</div>
          <div className="zusatz">
            {euro(e.hinweise.offenePosten.einnahmen)} zu erhalten,{' '}
            {euro(e.hinweise.offenePosten.ausgaben)} zu zahlen
          </div>
        </div>
      </div>
    </>
  );
}

// =========================================================================
//  Grenzprüfung nach § 19 UStG
// =========================================================================

function Grenzkarte({ abruf }: { abruf: ReturnType<typeof useAbruf<Grenzpruefung>> }): ReactNode {
  if (!abruf.daten) return null;
  const g = abruf.daten;

  // Gemessen wird gegen die Vorjahresgrenze (25.000 €): Sie ist die
  // niedrigere und entscheidet darüber, ob die Regelung im kommenden Jahr
  // noch gilt.
  const anteil = g.grenzeVorjahrCent > 0 ? g.umsatzLaufendCent / g.grenzeVorjahrCent : 0;
  const prozent = Math.min(100, Math.round(anteil * 100));
  const klasse =
    g.status === 'ueberschritten' ? 'gefahr' : g.status === 'nahe_grenze' ? 'warnung' : '';

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Kleinunternehmergrenze</h2>
        <span className="klein still">§ 19 UStG</span>
      </div>
      <div className="karte-koerper stapel">
        {g.meldung ? (
          <Meldung ton={g.status === 'ueberschritten' ? 'gefahr' : 'warnung'}>{g.meldung}</Meldung>
        ) : null}

        <div>
          <div className="reihe verteilt klein">
            <span className="leise">
              Umsatz {g.jahr}: <strong>{g.anzeige.umsatzLaufend}</strong>
            </span>
            <span className="still">
              {prozent} % von {g.anzeige.grenzeVorjahr}
            </span>
          </div>
          <div className={`balken ${klasse}`}>
            <div style={{ width: `${prozent}%` }} />
          </div>
          <div className="klein still">
            Vorjahr: {g.anzeige.umsatzVorjahr} · laufende Grenze: {g.anzeige.grenzeLaufend}
          </div>
        </div>

        {g.status === 'unauffaellig' ? (
          <p className="klein still" style={{ margin: 0 }}>
            {g.hinweis}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// =========================================================================
//  Monatsübersicht
// =========================================================================

function Monatskarte({
  abruf,
}: {
  abruf: ReturnType<typeof useAbruf<Monatsuebersicht>>;
}): ReactNode {
  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Monate</h2>
      </div>
      {!abruf.daten ? (
        <Laedt />
      ) : (
        <MonatsBalken daten={abruf.daten} />
      )}
    </div>
  );
}

function MonatsBalken({ daten }: { daten: Monatsuebersicht }): ReactNode {
  const werte = daten.monate.map((m) => ({
    ...m,
    einCent: alsCent(m.einnahmen),
    ausCent: alsCent(m.ausgaben),
  }));
  const groesster = Math.max(1, ...werte.map((w) => Math.max(w.einCent, w.ausCent)));

  if (werte.every((w) => w.anzahlBelege === 0)) {
    return (
      <Leer
        titel="Noch keine gebuchten Belege in diesem Jahr"
        hinweis="Gezählt wird nach Zahlungsdatum — ein erfasster, aber unbezahlter Beleg erscheint hier nicht."
      />
    );
  }

  return (
    <div className="karte-koerper">
      <table className="tabelle">
        <thead>
          <tr>
            <th>Monat</th>
            <th>Verlauf</th>
            <th className="zahl">Einnahmen</th>
            <th className="zahl">Ausgaben</th>
            <th className="zahl">Ergebnis</th>
          </tr>
        </thead>
        <tbody>
          {werte.map((m) => (
            <tr key={m.monat}>
              <td className="schmal">{monatsname(m.monat).slice(0, 3)}</td>
              <td style={{ minWidth: '90px' }}>
                {/* Zwei Balken statt eines Diagramms: Eine Zeichenbibliothek
                    für zwölf Werte wäre unverhältnismäßig. */}
                <div className="balken" style={{ height: '5px', margin: '3px 0' }}>
                  <div style={{ width: `${(m.einCent / groesster) * 100}%` }} />
                </div>
                <div className="balken gefahr" style={{ height: '5px', margin: '3px 0' }}>
                  <div style={{ width: `${(m.ausCent / groesster) * 100}%` }} />
                </div>
              </td>
              <td className="zahl">{m.einCent === 0 ? '—' : formatiereCent(m.einCent, false)}</td>
              <td className="zahl">{m.ausCent === 0 ? '—' : formatiereCent(m.ausCent, false)}</td>
              <td className="zahl">{formatiereCent(m.einCent - m.ausCent, false)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Summe</td>
            <td className="zahl">
              {formatiereCent(
                werte.reduce((s, m) => s + m.einCent, 0),
                false,
              )}
            </td>
            <td className="zahl">
              {formatiereCent(
                werte.reduce((s, m) => s + m.ausCent, 0),
                false,
              )}
            </td>
            <td className="zahl">
              {formatiereCent(
                werte.reduce((s, m) => s + m.einCent - m.ausCent, 0),
                false,
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// =========================================================================
//  Offene Posten
// =========================================================================

function OffeneKarte({ abruf }: { abruf: ReturnType<typeof useAbruf<OffenePosten>> }): ReactNode {
  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Offene Posten</h2>
        {abruf.daten ? (
          <span className="klein still">
            {abruf.daten.summe.forderungen} / {abruf.daten.summe.verbindlichkeiten}
          </span>
        ) : null}
      </div>

      {!abruf.daten ? (
        <Laedt />
      ) : abruf.daten.posten.length === 0 ? (
        <Leer titel="Nichts offen" hinweis="Alle erfassten Belege haben ein Zahlungsdatum." />
      ) : (
        <div className="tabelle-rahmen">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Vorgang</th>
                <th className="schmal">Datum</th>
                <th className="zahl">Betrag</th>
                <th className="schmal">Offen</th>
              </tr>
            </thead>
            <tbody>
              {abruf.daten.posten.slice(0, 10).map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={`/belege/${p.id}`} className="haupttext">
                      {p.bezeichnung}
                    </Link>
                    <div className="nebentext">
                      {p.rechnungsnummer ?? p.belegnummer ?? 'ohne Nummer'}
                      {p.kontakt ? ` · ${p.kontakt}` : ''}
                    </div>
                  </td>
                  <td className="schmal">{datum(p.beleg_datum)}</td>
                  <td className="zahl">
                    {p.richtung === 'ausgabe' ? '−' : ''}
                    {euro(p.betrag_brutto, false)}
                  </td>
                  <td className="schmal">
                    {p.ueberfaellig ? (
                      <span className="marker gefahr">{zahl(p.tage_offen, 0)} Tage</span>
                    ) : (
                      <span className="still klein">{zahl(p.tage_offen, 0)} Tage</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {abruf.daten.posten.length > 10 ? (
            <div className="blaetterleiste">
              <span>
                10 von {abruf.daten.posten.length} angezeigt
              </span>
              <Link to="/belege?offen=true" className="knopf klein">
                Alle offenen Posten
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =========================================================================
//  EÜR-Positionen
// =========================================================================

function Positionskarte({
  abruf,
  jahr,
}: {
  abruf: ReturnType<typeof useAbruf<Euer>>;
  jahr: number;
}): ReactNode {
  if (!abruf.daten || abruf.daten.positionen.length === 0) return null;

  const einnahmen = abruf.daten.positionen.filter((p) => p.richtung === 'einnahme');
  const ausgaben = abruf.daten.positionen.filter((p) => p.richtung === 'ausgabe');

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>EÜR-Positionen {jahr}</h2>
        <Link to="/auswertung" className="knopf klein">
          Vollständige Auswertung
        </Link>
      </div>
      <div className="tabelle-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th className="schmal">Zeile</th>
              <th>Position</th>
              <th className="zahl schmal">Belege</th>
              <th className="zahl">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {[
              { titel: 'Betriebseinnahmen', zeilen: einnahmen },
              { titel: 'Betriebsausgaben', zeilen: ausgaben },
            ].map((bereich) =>
              bereich.zeilen.length === 0 ? null : (
                <Fragment key={bereich.titel}>
                  <tr>
                    <td colSpan={4} className="leise klein" style={{ paddingTop: '0.75rem' }}>
                      <strong>{bereich.titel}</strong>
                    </td>
                  </tr>
                  {bereich.zeilen.map((p) => (
                    <tr key={`${bereich.titel}-${p.position ?? p.bezeichnung}`}>
                      {/* zeile = 0 heißt „noch nicht zugeordnet" (siehe
                          002-stammdaten.sql). Als Zahl angezeigt sähe das
                          wie eine echte Zeilennummer aus — und stünde
                          womöglich so in ELSTER. */}
                      <td className="schmal still">
                        {p.zeile && p.zeile > 0 ? p.zeile : '—'}
                      </td>
                      <td>
                        <span className="haupttext">{p.bezeichnung}</span>
                        <div className="nebentext">
                          {p.kategorien.map((k) => k.bezeichnung).join(', ')}
                        </div>
                      </td>
                      <td className="zahl schmal still">{p.anzahl}</td>
                      <td className="zahl">{euro(p.betrag, false)}</td>
                    </tr>
                  ))}
                </Fragment>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
